import "./payment-section-gift-card.css";

import EventEmitter from "eventemitter3";
import { DialogCreators } from "../../../../../utils/dialog-creators";
import { TwoWayBindingHelper } from "../../../../../utils/two-way-binding";
import { Util } from "../../../../../utils/util";
import { IPaymentSectionCallbacks, IPaymentSectionModel } from "./payment-section.interface";
import { Data } from "../../../../../models/data";
import { Context } from "../../../../../models/context";
import { LocalizationService } from "../../../../../services/localization-service";
import { Order } from "../../../../../models/order";
import { DataLayerService } from "../../../../../services/data-layer-service";
import { OnlineOrderingService } from "../../../../../services/online-ordering-service";
import { FieldType, MerchantProcessor } from "../../../../../types/enums";
import { IValidationField } from "../../../../../validators/validation-field.interface";
import { InputValidator } from "../../../../../validators/input-validator";
import { IOrderGiftCard } from "../../../../../models/order.interface";
import { IServiceGetGiftCardBalance } from "../../../../../services/online-ordering-service.interface";
import { GiftCardCard } from "../../../../shared/GiftCardCard/gift-card-card";
import { IDataMerchant } from "../../../../../models/data.interface";

export class PaymentSectionGiftCard {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _order: Order;
    private readonly _dataLayer: DataLayerService;
    private readonly _onlineOrderingService: OnlineOrderingService;
    private readonly _model: IPaymentSectionModel;
    private readonly _bindingHelper: TwoWayBindingHelper<IPaymentSectionModel>;
    private readonly _callbacks: IPaymentSectionCallbacks;

    constructor(
        eventBus: EventEmitter,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
        order: Order,
        dataLayer: DataLayerService,
        onlineOrderingService: OnlineOrderingService,
        callbacks: IPaymentSectionCallbacks
    ) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._order = order;
        this._dataLayer = dataLayer;
        this._onlineOrderingService = onlineOrderingService;
        this._callbacks = callbacks;
        this._model = this._createModel();
        this._bindingHelper = new TwoWayBindingHelper(this._model);
    }

    // ─── Public getters ───────────────────────────────────────────────────────────

    get isGiftCard(): boolean { return this._order.getProperty("GiftCards")?.length > 0 }

    // ─── Lifecycle ────────────────────────────────────────────────────────────────

    public resetModel(): void {
        const fresh = this._createModel();
        this._model.giftCardNumber = fresh.giftCardNumber;
        this._model.giftCardPin = fresh.giftCardPin;
    }

    public setupEventListeners(): void {
        Util.setElement("onclick", "btn_checkout_gift_card_open", this._openGiftCardSectionOnClick);
        Util.setElement("onclick", "btn_checkout_gift_cards_close", this._closeGiftCardSectionOnClick);
        Util.setElement("onclick", "btn_checkout_gift_card_apply", this._applyGiftCardOnClickAsync);
    }

    public setupTwoWayListeners(): void {
        this._bindingHelper.bindToInput("giftCardNumber", "txt_checkout_gift_card_number");
        this._bindingHelper.bindToInput("giftCardPin", "txt_checkout_gift_card_pin");
    }

    // ─── Render ───────────────────────────────────────────────────────────────────

    public renderGiftCards(): void {
        const merchant = this._getGiftCardMerchant();
        if (!merchant) {
            this._model.isGiftCard = false;
            Util.hideElement("div_checkout_gift_card_toggle");
            Util.hideElement("div_checkout_gift_cards");
            this._order.removeAllGiftCards();
            return;
        }

        if (this._model.isGiftCard) {
            this._model.giftCardNumber = "";
            this._model.giftCardPin = "";

            this._displayPin();

            Util.hideElement("div_checkout_gift_card_toggle");
            Util.showElement("div_checkout_gift_cards");
            this._displayAppliedGiftCards();
        } else {
            Util.showElement("div_checkout_gift_card_toggle");
            Util.hideElement("div_checkout_gift_cards");
        }
    }

    // ─── Validation helpers for checkout's _checkSubmitDataAsync ──────────────────

    /** Returns false (with a dialog) when no payment option is selected and order isn't fully gift-card-paid. */
    public validateGiftCardPayment(): boolean {
        const paidByGiftCard = this._checkOrderPaidByGiftCard();
        if (this._callbacks.getPaymentOption() === null && !paidByGiftCard) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("NoPayment"),
                true
            );
            return false;
        }
        if (paidByGiftCard)
            this._callbacks.setPaymentOption(null);
        return true;
    }


    // ─── Private helpers ──────────────────────────────────────────────────────────

    private _createModel(): IPaymentSectionModel {
        return {
            isGiftCard: false,
            giftCardNumber: '',
            giftCardPin: ''
        };
    }

    private readonly _openGiftCardSectionOnClick = (): void => {
        this._model.isGiftCard = true;
        this._displayPin();
        Util.hideElement("div_checkout_gift_card_toggle");
        Util.showElement("div_checkout_gift_cards");
    }

    private readonly _closeGiftCardSectionOnClick = (): void => {
        this._model.isGiftCard = false;
        Util.hideElement("div_checkout_gift_cards");
        Util.showElement("div_checkout_gift_card_toggle");
    }

    private readonly _applyGiftCardOnClickAsync = async (): Promise<void> => {
        Util.resetAnnounceCycle();

        if (!(await this._validateGiftCardsAsync())) {
            Util.scrollToFirstInvalidElement("div_checkout_gift_cards");
            Util.announceErrors(Util.collectVisibleErrors("div_checkout_gift_cards"));
            return;
        }

        const clearCardNumber = Util.cleanNonDigits(this._model.giftCardNumber.trim());
        const clearPin = Util.cleanNonDigits(this._model.giftCardPin.trim());

        const payload = {
            SKY: this._data.getProperty("StoreInfo").storeKey,
            CNO: clearCardNumber,
            PIN: clearPin
        };

        const response = await this._onlineOrderingService.getGiftCardBalance(payload);
        if (response.balance === 0) {
            DialogCreators.messageBoxOk(this._localizationService, this._dataLayer, this._localizationService.names("Error_GiftCardBalanceIs0"), true);
            return;
        }

        if (response.success) {
            this._addGiftCardToOrder(response, payload.CNO, payload.PIN);
            if (this._dataLayer)
                this._dataLayer.add_payment_info(this._order, this._callbacks.getIsCurbside());
        } else if (response.message) {
            DialogCreators.messageBoxOk(this._localizationService, this._dataLayer, response.message, true);
        }
    }

    private readonly _validateGiftCardsAsync = async (): Promise<boolean> => {
        const merchant = this._getGiftCardMerchant();
        const isValutec = merchant?.ProcessorId === MerchantProcessor.Valutec;

        if (this._model.giftCardNumber.trim().length === 0) {
            Util.setElement("innerText", "spn_checkout_gift_card_number_error", this._localizationService.names("GIFT_CARD_REQUIRED"));
            document.getElementById("spn_checkout_gift_card_number_error")?.setAttribute("ltag", "GIFT_CARD_REQUIRED");
        } else {
            Util.setElement("innerText", "spn_checkout_gift_card_number_error", this._localizationService.names("InvalidCardNumber"));
            document.getElementById("spn_checkout_gift_card_number_error")?.setAttribute("ltag", "InvalidCardNumber");
        }

        if (this._model.giftCardPin.trim().length === 0 && !isValutec) {
            Util.setElement("innerText", "spn_checkout_gift_card_pin_error", this._localizationService.names("GIFT_CARD_PIN_REQUIRED"));
            document.getElementById("spn_checkout_gift_card_pin_error")?.setAttribute("ltag", "GIFT_CARD_PIN_REQUIRED");
        } else {
            Util.setElement("innerText", "spn_checkout_gift_card_pin_error", this._localizationService.names("InvalidPin"));
            document.getElementById("spn_checkout_gift_card_pin_error")?.setAttribute("ltag", "InvalidPin");
        }

        const fields: IValidationField[] = [
            { type: FieldType.GIFT_CARD_NUMBER, inputId: "txt_checkout_gift_card_number", errorId: "spn_checkout_gift_card_number_error" }
        ];

        const cardNumber = this._model.giftCardNumber.trim();
        if (this._isGiftCardApplied(cardNumber))
            fields.push({ type: FieldType.FAILED, inputId: "txt_checkout_gift_card_number", errorId: "spn_checkout_gift_card_number_duplicate_error" });

        if (this._getGiftCardHasPin()) {
            const type = isValutec ? FieldType.GIFT_CARD_PIN_VALUTEC : FieldType.GIFT_CARD_PIN;
            fields.push({ type, inputId: "txt_checkout_gift_card_pin", errorId: "spn_checkout_gift_card_pin_error" });
        }

        const failed = fields.map(field => InputValidator.validate(
            field.type, field.inputId, field.errorId,
            undefined, undefined, undefined, undefined, this._localizationService, this._dataLayer
        )).some(Boolean);

        return !failed;
    }

    private readonly _isGiftCardApplied = (cardNumber: string): boolean => {
        return this._order.getProperty("GiftCards").some(x => x.cardNo === cardNumber);
    }

    private _displayPin(): void {
        if (this._getGiftCardHasPin())
            Util.showElement("div_checkout_gift_card_pin");
        else
            Util.hideElement("div_checkout_gift_card_pin");
    }

    private readonly _addGiftCardToOrder = (response: IServiceGetGiftCardBalance, cardNumber: string, pin: string): void => {
        this._model.giftCardNumber = "";
        this._model.giftCardPin = "";
        const randomString = (Math.floor(Math.random() * 100) + 1).toString();

        const giftCard: IOrderGiftCard = {
            cardNo: cardNumber,
            balance: response.balance,
            pin,
            appliedAmount: 0,
            randomId: randomString
        };

        this._order.addGiftCard(giftCard);
        if (this._order.getProperty("AmountDue") === 0)
            this._order.setProperty("PaymentType", undefined);
        this._displayAppliedGiftCards();
    }

    private readonly _displayAppliedGiftCards = (): void => {
        const div = document.getElementById("div_checkout_gift_card_applied");
        if (!div) return;

        div.innerHTML = "";
        const giftCardsApplied = this._order.getProperty("GiftCards");

        for (const giftCard of giftCardsApplied) {
            const cardClass = new GiftCardCard(this._eventBus, this._data, this._localizationService, this._order, giftCard.cardNo, this._removeGiftCard);
            if (cardClass.element)
                div.appendChild(cardClass.element);
        }

        if (giftCardsApplied.length == 0)
            Util.showElement("btn_checkout_gift_cards_close");
        else
            Util.hideElement("btn_checkout_gift_cards_close");
    }

    private readonly _removeGiftCard = (cardNumber: string): void => {
        this._order.removeGiftCard(cardNumber);
        this._displayAppliedGiftCards();
    }

    private _getGiftCardMerchant(): IDataMerchant | undefined {
        return this._data.getProperty("Merchants").find(x => x.TransactionType.toUpperCase().includes('J'));
    }

    private _getGiftCardHasPin(): boolean {
        return this._getGiftCardMerchant()?.isGiftCardPin ?? false;
    }

    private _checkOrderPaidByGiftCard(): boolean {
        if (this._context.getProperty("isOrderHub")) return false;
        const total = this._order.calculateOrderTotal();
        let giftCardTotal = 0;
        for (const giftCard of this._order.getProperty("GiftCards"))
            giftCardTotal += giftCard.appliedAmount;
        return total == giftCardTotal;
    }
}